/**
 * Settings — manage platform-operator accounts.
 *   - list operators (email, name, status, last login)
 *   - add new operator (email + password + name)
 *   - change own / others' password
 *   - enable / disable / delete (guards keep ≥1 active admin, can't self-delete)
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, KeyRound, Power, Trash2, ShieldCheck } from 'lucide-react';
import { apiFetch, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/store';

interface AdminRow {
  id: string;
  email: string;
  display_name: string | null;
  status: 'active' | 'disabled';
  last_login_at: string | null;
  created_at: string;
}

export function Settings() {
  const qc = useQueryClient();
  const myId = useAuthStore((s) => s.profile?.user_id);
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'admins'],
    queryFn: () => apiFetch<{ admins: AdminRow[] }>('/platform/admins'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
  const flash = (kind: 'ok' | 'err', msg: string) => {
    setBanner({ kind, msg });
    setTimeout(() => setBanner(null), 4000);
  };

  // --- add ---
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const create = useMutation({
    mutationFn: () =>
      apiFetch('/platform/admins', { method: 'POST', body: { email: email.trim(), password: pw, display_name: name.trim() || undefined } }),
    onSuccess: () => {
      flash('ok', `เพิ่มผู้ดูแล ${email} แล้ว`);
      setEmail(''); setName(''); setPw('');
      invalidate();
    },
    onError: (e) => flash('err', e instanceof ApiError ? e.message : 'เพิ่มไม่สำเร็จ'),
  });

  const patch = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      apiFetch(`/platform/admins/${args.id}`, { method: 'PATCH', body: args.body }),
    onSuccess: () => { flash('ok', 'บันทึกแล้ว'); invalidate(); },
    onError: (e) => flash('err', e instanceof ApiError ? e.message : 'แก้ไขไม่สำเร็จ'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/platform/admins/${id}`, { method: 'DELETE' }),
    onSuccess: () => { flash('ok', 'ลบแล้ว'); invalidate(); },
    onError: (e) => flash('err', e instanceof ApiError ? e.message : 'ลบไม่สำเร็จ'),
  });

  function changePassword(a: AdminRow) {
    const np = prompt(`ตั้งรหัสผ่านใหม่สำหรับ ${a.email} (อย่างน้อย 8 ตัว)`);
    if (np == null) return;
    if (np.length < 8) { flash('err', 'รหัสผ่านต้องยาวอย่างน้อย 8 ตัว'); return; }
    patch.mutate({ id: a.id, body: { password: np } });
  }
  function renameAdmin(a: AdminRow) {
    const nn = prompt('ชื่อที่แสดง', a.display_name ?? '');
    if (nn == null) return;
    patch.mutate({ id: a.id, body: { display_name: nn } });
  }

  const admins = data?.admins ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand-700" /> ตั้งค่าผู้ดูแลระบบ
        </h1>

        {banner && (
          <div className={`text-sm rounded-lg px-3 py-2 ${banner.kind === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
            {banner.msg}
          </div>
        )}

        {/* Add new */}
        <div className="card p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> เพิ่มผู้ดูแลใหม่
          </h2>
          <form
            onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-2"
          >
            <input
              type="email" required placeholder="อีเมล" autoComplete="off"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="text" placeholder="ชื่อที่แสดง (ไม่บังคับ)"
              value={name} onChange={(e) => setName(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="password" required placeholder="รหัสผ่าน (≥ 8 ตัว)" autoComplete="new-password"
              value={pw} onChange={(e) => setPw(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button type="submit" disabled={create.isPending} className="btn-primary text-sm sm:col-span-3">
              {create.isPending ? 'กำลังเพิ่ม…' : 'เพิ่มผู้ดูแล'}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="card p-4">
          <h2 className="font-semibold mb-3">ผู้ดูแลทั้งหมด ({admins.length})</h2>
          {isLoading ? (
            <p className="text-center text-gray-500 py-6">กำลังโหลด…</p>
          ) : (
            <div className="space-y-2">
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {a.display_name ?? a.email}
                      {a.id === myId && <span className="text-xs text-brand-700 ml-2">(คุณ)</span>}
                      {a.status === 'disabled' && <span className="badge ml-2 bg-gray-200 text-gray-600">ปิดใช้งาน</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {a.email}
                      {a.last_login_at && <> · เข้าล่าสุด {new Date(a.last_login_at).toLocaleDateString('th-TH')}</>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => renameAdmin(a)} disabled={patch.isPending} className="btn-secondary text-xs" title="แก้ชื่อ">ชื่อ</button>
                    <button onClick={() => changePassword(a)} disabled={patch.isPending} className="btn-secondary text-xs" title="เปลี่ยนรหัสผ่าน">
                      <KeyRound className="w-3.5 h-3.5" /> รหัส
                    </button>
                    {a.status === 'active' ? (
                      <button
                        onClick={() => patch.mutate({ id: a.id, body: { status: 'disabled' } })}
                        disabled={patch.isPending || a.id === myId}
                        className="btn-secondary text-xs text-orange-700 border-orange-200 disabled:opacity-40"
                        title={a.id === myId ? 'ปิดบัญชีตัวเองไม่ได้' : 'ปิดใช้งาน'}
                      >
                        <Power className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => patch.mutate({ id: a.id, body: { status: 'active' } })}
                        disabled={patch.isPending}
                        className="btn-secondary text-xs text-emerald-700 border-emerald-200"
                        title="เปิดใช้งาน"
                      >
                        <Power className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => { if (confirm(`ลบผู้ดูแล ${a.email}?`)) remove.mutate(a.id); }}
                      disabled={remove.isPending || a.id === myId}
                      className="btn-secondary text-xs text-red-700 border-red-200 disabled:opacity-40"
                      title={a.id === myId ? 'ลบบัญชีตัวเองไม่ได้' : 'ลบ'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
